import { cloneDeep, debounce } from 'lodash';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useCepTemplateListConfig from './CepTemplateList.config';
import { PAGINATION_PAGE_SIZE } from 'basics/constants/common.constant';
import { mayaCepDuplicateRoute, mayaCepTemplateCreateRoute, mayaCepTemplateEditRoute } from 'basics/constants/routes.constants';
import { AllValidTherapeuticArea } from 'basics/enums/biogen.enums';
import { BiogenFunction } from 'basics/enums/maya.enums';
import { useSetCepStatusToDeletedMutation } from 'basics/graphql/mutations/setCepStatusToDeleted';
import useGetAllCepQuery from 'basics/graphql/queries/getAllCeps';
import { SelectOption } from 'basics/options/options.types';
import { ProductsOptionType } from 'basics/types/products.types';
import { succesToast, warningToast } from 'basics/utils/toast';
import { CustomerEngagementPlanning, SortFilter } from 'generated/maya.types';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useCepTemplateList = () => {
  const { providerFields, setValue } = useYodaCreateForm();
  const [cepSortFilter, setCepSortFilter] = useState(SortFilter.NEWEST);
  const [cepSearchFilter, setCepSearchFilter] = useState<(string)>();
  const [customerInitiativeFilter] = useState<(string | number)[]>([]);
  const [cepTherapeuticAreaFilter, setCepTherapeuticAreaFilter] = useState<string[]>([]);
  const [cepProductFilter, setCepProductFilter] = useState<string[]>([]);
  const [cepBiogenFunctionFilter, setCepBiogenFunctionFilter] = useState<BiogenFunction[]>([]);
  const [statusFilter] = useState<(string | number)[]>([]);
  const [ceps, setCeps] = useState<CustomerEngagementPlanning[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<{
    therapeuticArea: SelectOption[];
    product: SelectOption[];
    cepFunction: SelectOption[];
  }>({
    therapeuticArea: [],
    product: [],
    cepFunction: [],
  });
  const hasActiveFilters = Object.values(selectedFilters).some((options) => options.length > 0);

  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_PAGE_SIZE);
  const [rowCountState, setRowCountState] = useState(0);
  const [open, setOpen] = useState(false);
  const [rowId, setRowId] = useState('');

  const navigate = useNavigate();

  const queryOptions = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
    }),
    [page, pageSize],
  );

  const queryFilters = useMemo(
    () => ({
      search: cepSearchFilter,
      therapeuticArea: cepTherapeuticAreaFilter.length ? cepTherapeuticAreaFilter : null,
      product: cepProductFilter.length ? cepProductFilter : null,
      cepFunction: cepBiogenFunctionFilter.length ? cepBiogenFunctionFilter : null,
      getPopularity: true,
      isTemplate: true,
    }),
    [cepSearchFilter, cepTherapeuticAreaFilter, cepProductFilter, cepBiogenFunctionFilter],
  );

  const { data: cepData, loading: cepLoading, count } = useGetAllCepQuery(queryFilters, queryOptions, true, cepSortFilter);
  const [setCepStatusToDeleted, { loading: removeCepLoading }] = useSetCepStatusToDeletedMutation();

  useEffect(() => {
    setRowCountState((prevRowCountState) => (count !== undefined ? count : prevRowCountState));
  }, [count, setRowCountState]);

  useEffect(() => {
    if (!cepLoading) {
      setCeps(cepData);
    }
  }, [cepData, cepLoading]);

  const editCepCallback = async (cepId: string) => {
    navigate(`${mayaCepTemplateEditRoute}?id=${cepId}`);
  };

  const duplicateCepCallback = async (cepId: string) => {
    navigate(`${mayaCepDuplicateRoute}?cepSourceId=${cepId}`);
  };

  const deleteCepCallback = async (cepId: string) => {
    const { data: cepDeleted, error: cepDeletedError } = await setCepStatusToDeleted(cepId);
    if (cepDeleted && !cepDeletedError) {
      succesToast(t('maya_action_deleting_cep_success'));
      const clonedCeps = cloneDeep(ceps);
      const targetCepIndex = clonedCeps.findIndex((cep) => { return cep._id === cepId; });
      if (targetCepIndex !== -1) {
        clonedCeps.splice(targetCepIndex, 1);
        setCeps(clonedCeps);
      }
    } else {
      warningToast(cepDeletedError || t('errors_standard'));
    }

    setOpen(false);
  };

  const openModalCallback = (cepId: string) => {
    setOpen(true);
    setRowId(cepId);
  };

  const {
    cepListColumnConfig,
    cepTitleFieldConfig,
    cepTherapeuticAreaFieldConfig,
    cepProductFieldConfig,
    cepBiogenFunctionFieldConfig,
    cepSortFieldConfig,
  } = useCepTemplateListConfig(
    editCepCallback,
    duplicateCepCallback,
    openModalCallback,
    cepTherapeuticAreaFilter,
  );

  const newCepClick = () => {
    navigate(mayaCepTemplateCreateRoute);
  };

  const handleChangeCepSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setCepSearchFilter(event.target.value);
  };
  const debouncedHandleChangeCepSearch = useMemo(
    () => debounce(handleChangeCepSearch, 750),
    [],
  );

  const handleChangeCepTherapeuticArea = useCallback((event: SyntheticEvent<Element, Event>, value: SelectOption[]) => {
    if (value.length === 0) {
      setCepProductFilter([]);
      setValue(cepProductFieldConfig.name, []);
      setSelectedFilters((prev) => ({ ...prev, product: [] }));
    }

    const values = value.map((element) => element.id as string);

    if (values.includes(AllValidTherapeuticArea.BBU)) {
      values.push(AllValidTherapeuticArea.BIOSIMS);
    }

    setCepTherapeuticAreaFilter(values);
    setSelectedFilters((prev) => ({ ...prev, therapeuticArea: value }));
  }, [setCepTherapeuticAreaFilter, setValue, cepProductFieldConfig.name]);

  const handleChangeCepProduct = useCallback((event: SyntheticEvent<Element, Event>, product: ProductsOptionType[]) => {
    const values = product.map((element) => element.id as string);
    setCepProductFilter(values);
    setSelectedFilters((prev) => ({ ...prev, product }));
  }, [setCepProductFilter]);

  const handleChangeCepBiogenFunction = useCallback((event: SyntheticEvent<Element, Event>, value: SelectOption[]) => {
    const values = value.map((element) => element.id as BiogenFunction);
    setCepBiogenFunctionFilter(values);
    setSelectedFilters((prev) => ({ ...prev, cepFunction: value }));
  }, [setCepBiogenFunctionFilter]);

  const getFilteredRows = useCallback(() => {
    let filteredRows = ceps;

    if (customerInitiativeFilter.length > 0) {
      filteredRows = cepData.filter((value) => {
        if (typeof value.customerInitiative === 'string') {
          return value.customerInitiative;
        }

        return customerInitiativeFilter.indexOf(value.customerInitiative._id) !== -1;
      });
    }

    if (statusFilter.length > 0) {
      filteredRows = filteredRows.filter((value) => {
        return statusFilter.indexOf(value.status) !== -1;
      });
    }

    return filteredRows;
  }, [ceps, customerInitiativeFilter, statusFilter, cepData]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const menuCustomerEngagementPlanningLabel = t('maya_main_menu_cep_tpl_label');
  const newElementButton = {
    newElementLabel: t('maya_list_new_element_label', { elementTitle: 'Template' }),
    newElementClick: newCepClick,
  };

  const handleSortChange = useCallback((value: SelectOption) => {
    setCepSortFilter(value.id as SortFilter);
  }, []);

  const handleRemoveChip = useCallback((filterType: 'therapeuticArea' | 'product' | 'cepFunction', idToRemove: string | number) => {
    if (filterType === 'therapeuticArea') {
      const newOptions = selectedFilters.therapeuticArea.filter((o) => o.id !== idToRemove);
      const values = newOptions.map((element) => element.id as string);

      if (values.includes(AllValidTherapeuticArea.BBU)) {
        values.push(AllValidTherapeuticArea.BIOSIMS);
      }

      setCepTherapeuticAreaFilter(values);
      setSelectedFilters((prev) => ({ ...prev, therapeuticArea: newOptions }));
      setValue(cepTherapeuticAreaFieldConfig.name, newOptions);

      if (newOptions.length === 0) {
        setCepProductFilter([]);
        setSelectedFilters((prev) => ({ ...prev, product: [] }));
        setValue(cepProductFieldConfig.name, []);
      }
    } else if (filterType === 'product') {
      const newOptions = selectedFilters.product.filter((o) => o.id !== idToRemove);
      const values = newOptions.map((product) => product.id as string);

      setCepProductFilter(values);
      setSelectedFilters((prev) => ({ ...prev, product: newOptions }));
      setValue(cepProductFieldConfig.name, newOptions);
    } else if (filterType === 'cepFunction') {
      const newOptions = selectedFilters.cepFunction.filter((o) => o.id !== idToRemove);
      const values = newOptions.map((element) => element.id as BiogenFunction);
      setCepBiogenFunctionFilter(values);
      setSelectedFilters((prev) => ({ ...prev, cepFunction: newOptions }));
      setValue(cepBiogenFunctionFieldConfig.name, newOptions);
    }
  }, [selectedFilters, setValue, cepTherapeuticAreaFieldConfig.name, cepProductFieldConfig.name, cepBiogenFunctionFieldConfig.name]);

  const handleReset = useCallback(() => {
    setCepTherapeuticAreaFilter([]);
    setCepProductFilter([]);
    setCepBiogenFunctionFilter([]);
    setSelectedFilters({
      therapeuticArea: [],
      product: [],
      cepFunction: [],
    });
    setValue(cepTherapeuticAreaFieldConfig.name, []);
    setValue(cepProductFieldConfig.name, []);
    setValue(cepBiogenFunctionFieldConfig.name, []);
  }, [setValue, cepTherapeuticAreaFieldConfig.name, cepProductFieldConfig.name, cepBiogenFunctionFieldConfig.name]);

  const hasSelectedFilters = selectedFilters.therapeuticArea.length > 0
    || selectedFilters.product.length > 0
    || selectedFilters.cepFunction.length > 0;

  return {
    cepData,
    cepListColumnConfig,
    cepLoading,
    cepTitleFieldConfig,
    customerInitiativeFilter,
    debouncedHandleChangeCepSearch,
    deleteCepCallback,
    cepTherapeuticAreaFieldConfig,
    handleChangeCepTherapeuticArea,
    cepProductFieldConfig,
    handleChangeCepProduct,
    cepBiogenFunctionFieldConfig,
    handleChangeCepBiogenFunction,
    getFilteredRows,
    menuCustomerEngagementPlanningLabel,
    newElementButton,
    onPageChange,
    onPageSizeChange,
    open,
    removeCepLoading,
    rowCountState,
    rowId,
    setOpen,
    statusFilter,
    providerFields,
    selectedFilters,
    handleRemoveChip,
    handleReset,
    hasSelectedFilters,
    cepSortFieldConfig,
    handleSortChange,
    hasActiveFilters,
    cepSortFilter,
  };
};

export default useCepTemplateList;
